import React, {
  useEffect,
  useState,
} from 'react';

import {
  fetchAuthSession,
  getCurrentUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  createTheme,
  ThemeProvider,
} from '@mui/material/styles';

// Import Components
import Dashboard from './Dashboard';
import LoadingScreen from './LoadingScreen';
import Login from './Login';
import Signup from './Signup';

// Create theme
const theme = createTheme({
  // ... theme configuration
  palette: {
    primary: {
      main: '#0055A4', // Main blue color
    },
    secondary: {
      main: '#28C76F', // Green for success/savings
    },
    background: {
      default: '#F4F4F4',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121',
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
        
        // Verify the session is valid
        try {
          await fetchAuthSession();
          setIsAuthenticated(true);
        } catch (sessionError) {
          console.error("Session validation failed:", sessionError);
          setIsAuthenticated(false);
          navigate('/login', { state: { from: location, error: 'Your session has expired. Please log in again.' } });
        }
        
        setChecking(false);
      } catch (error) {
        console.error("Authentication check failed:", error);
        setIsAuthenticated(false);
        setChecking(false);
        navigate('/login', { state: { from: location } });
      }
    };
    
    checkAuth();
    
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      console.log('Auth event:', event);
      
      if (event === 'signedIn') {
        setIsAuthenticated(true);
      } else if (event === 'signedOut') {
        setIsAuthenticated(false);
        navigate('/login');
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [navigate, location]);
  
  if (checking) {
    return <LoadingScreen />;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" state={{ from: location }} />;
};

// App component
const AppRouter = () => {
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await fetchAuthSession();
        console.log("Valid auth session found");
        setIsLoading(false);
      } catch (error) {
        console.log("No valid auth session:", error);
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default AppRouter;